<template>
  <div class="rm-screen">
    <div v-if="loading" class="rm-empty">
      <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
    </div>

    <template v-else>
      <div class="rm-stats">
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("ordered") }}</span>
          <span class="rm-stat__value rm-num">{{ stats.ordered }}</span>
        </div>
        <div class="rm-stat">
          <span class="rm-stat__label">{{ $t("taken") }}</span>
          <span class="rm-stat__value rm-num">{{ stats.taken }}</span>
        </div>
        <div class="rm-stat rm-stat--accent">
          <span class="rm-stat__label">{{ $t("difference") }}</span>
          <span class="rm-stat__value rm-num">{{ stats.difference }}</span>
        </div>
      </div>

      <div class="rm-chip-row cm-emp-filters" role="group" :aria-label="$t('order_status')">
        <button
          v-for="item in statusChips"
          :key="item.value"
          type="button"
          class="rm-chip"
          :class="{ 'rm-chip--active': orderStatus === item.value }"
          :aria-pressed="String(orderStatus === item.value)"
          @click="orderStatus = item.value"
        >
          {{ item.text }}
          <span class="rm-num cm-emp-filters__n">{{ item.count }}</span>
        </button>
      </div>

      <template v-if="shownWorkers.length">
        <ul class="rm-list rm-card">
          <li v-for="worker in shownWorkers" :key="worker.id" class="cm-emp__row">
            <button type="button" class="cm-emp__row-btn" @click="openWorker(worker)">
              <span class="cm-emp__text">
                <span class="cm-emp__name">{{ worker.name }}</span>
                <span class="cm-emp__meta rm-num">{{ worker.meta }}</span>
                <span v-if="worker.shifts.length" class="cm-emp__shifts">
                  <report-shift-tag v-for="shift in worker.shifts" :key="shift" :shift="shift"></report-shift-tag>
                </span>
              </span>
              <span class="rm-status" :class="`rm-status--${worker.status}`">
                {{ worker.statusLabel }}
              </span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </li>
        </ul>
        <button v-if="hiddenCount > 0" type="button" class="rm-text-btn cm-emp__more" @click="expanded = true">
          {{ $t("report_mobile.show_more", { items: pluralT("report_mobile.workers_n", hiddenCount) }) }}
        </button>
      </template>
      <div v-else class="rm-empty">{{ emptyText }}</div>
    </template>
  </div>
</template>

<script>
import CompanyReportTabEmployees from "@/components/company/reports/companyTabs/companyReportTabEmployees.vue";
import hasWorkerList from "@/components/report/shared/hasWorkerList";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";

const PAGE_SIZE = 15;

/**
 * Mobilni "Radnici" firme: poručeno / preuzeto / razlika po radniku, uz one koji nisu poručili. Filter statusa
 * je red čipova sa brojem radnika; klik na radnika otvara njegov mesečni pregled. Nasleđuje učitavanje (radnici
 * izabranih firmi), štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyMobileEmployees",
  extends: CompanyReportTabEmployees,
  // Stari tab već prati ceo opseg datuma (dates, deep), pa mu hasPeriodRefetch ne treba.
  mixins: [hasWorkerList],
  components: { ReportShiftTag },
  data: () => ({
    expanded: false,
  }),
  computed: {
    /** Firma radnika za mesečni pregled: samo kad je izabrana jedna (inače se ne zna kojoj radnik pripada). */
    selectedCompany() {
      const companies = this.$store.state.report.selectedFoodCompanies || [];
      return companies.length === 1 ? companies[0] : null;
    },
    /** Svi radnici izabranog statusa (i oni bez narudžbe); status filtrira stari tab (orderStatus). */
    workers() {
      return this.filteredUsers.map((user) => this.toWorker(user));
    },
    tabWorkers() {
      return this.workers;
    },
    stats() {
      const ordered = this.workers.reduce((sum, worker) => sum + worker.ordered, 0);
      const taken = this.workers.reduce((sum, worker) => sum + worker.taken, 0);
      return { ordered, taken, difference: ordered - taken };
    },
    statusChips() {
      const users = (this.report && this.report.users) || [];
      const ordered = (user) => (user.order_count || 0) > 0;
      const taken = (user) => (user.order_payed || 0) > 0;
      const counts = {
        0: users.length,
        1: users.filter(ordered).length,
        2: users.filter((user) => !ordered(user)).length,
        3: users.filter((user) => ordered(user) && taken(user)).length,
        4: users.filter((user) => ordered(user) && !taken(user)).length,
      };
      return this.statusItems.map((item) => ({ ...item, count: counts[item.value] }));
    },
    shownWorkers() {
      if (this.expanded || this.filters.search) {
        return this.matchedWorkers;
      }
      return this.matchedWorkers.slice(0, PAGE_SIZE);
    },
    hiddenCount() {
      return this.matchedWorkers.length - this.shownWorkers.length;
    },
    emptyText() {
      if (this.filters.search) {
        return this.$t("report_mobile.no_results_for", { search: this.filters.search });
      }
      return this.report && this.report.users && this.report.users.length
        ? this.$t("report_mobile.no_workers_in_view")
        : this.$t("no_employee_report_data");
    },
  },
  watch: {
    orderStatus() {
      this.expanded = false;
    },
  },
  methods: {
    toWorker(user) {
      const worker = hasWorkerList.methods.toWorker.call(this, user);
      const ordered = worker.ordered > 0;
      return {
        ...worker,
        status: ordered ? worker.status : "idle",
        statusLabel: ordered ? worker.statusLabel : this.$t("report_desktop.status_not_ordered"),
        meta: [worker.meta, user.external_code, this.toLatin(user.company_name || "")].filter(Boolean).join(" · "),
      };
    },
    openWorker(worker) {
      this.$store.commit("report/SET_SELECTED_EMPLOYEE", this.toEmployeeContext(worker));
      this.$emit("open-screen", "employee");
    },
  },
};
</script>

<style lang="scss">
.rm-theme .cm-emp-filters__n {
  margin-left: 6px;
  font-weight: 700;
}

.rm-theme .rm-status--idle {
  color: var(--rm-chip-text);
  background: var(--rm-chip-bg);
}

.cm-emp {
  &__row + &__row {
    border-top: 1px solid var(--rm-divider-soft);
  }

  &__row-btn {
    width: 100%;
    min-height: 68px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px 10px 14px;
    border: none;
    background: transparent;
    text-align: left;
    color: var(--rm-text);

    &:active {
      background: var(--rm-bg);
    }
  }

  &__text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    overflow-wrap: anywhere;
  }

  &__meta {
    font-size: 12px;
    color: var(--rm-muted);
    overflow-wrap: anywhere;
  }

  &__shifts {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 2px;
  }

  &__more {
    align-self: stretch;
    height: 48px;
  }
}
</style>
